/**
 * EPIC-053 STORY-A — outcome icon resolution.
 *
 * Polymarket-parity: the multi-outcome market detail page shows a
 * small leading icon per outcome — country flags for "Quem ganhará a
 * Eurovision 2026?", glyphs for Yes / No / Draw / Over / Under, and
 * a 2-letter colored bubble fallback for everything else.
 *
 * Strategy (cheap + locale-tolerant):
 *
 *  1. **Country names → flag emoji.** A static ISO-3166 mapping that
 *     accepts the English country name, the Portuguese name, and a
 *     small set of common aliases (UK / USA / etc.). Returns the
 *     flag emoji as a plain string so callers don't need a sprite
 *     sheet or an image lookup.
 *  2. **Concepts → glyph emoji.** Yes / No / Other / Above / Below /
 *     Tie / Draw — the small set of patterns that show up across
 *     sports + economics + culture markets, with locale aliases
 *     (Sim / Não / Empate / Sí / Empate / etc.) so a pt-BR-locale
 *     outcome card picks up the glyph too.
 *  3. **Fallback bubble.** A `{ kind: "bubble", initials, color }`
 *     descriptor that the row component renders as a 28px colored
 *     circle with 2 letters inside (Polymarket / Stocktwits pattern
 *     for unmapped outcomes).
 *
 * The lookup is intentionally string-shaped — no image fetches, no
 * SSR-vs-CSR mismatches, no extra dependencies. The fallback bubble
 * color is stable across renders (hash → palette-index).
 *
 * When per-outcome `image_url` lands (future story), this helper
 * gets a fourth branch — `if (outcome.imageUrl) return { kind:
 * "image", url }` — and the row component renders the network image;
 * the existing flag + concept + bubble branches stay as the fallback
 * chain for outcomes without an admin-uploaded image.
 */

export type OutcomeIcon =
  | { kind: "emoji"; emoji: string }
  | { kind: "bubble"; initials: string; color: string };

// ─── Country flags (ISO-3166 + common aliases) ──────────────────
//
// Curated set covering Eurovision / FIFA / Olympics / political-
// election markets. Stored as lowercase-trim canonical keys; the
// lookup normalizes the caller's input the same way so "USA",
// "usa", and " usa " all hit the same row.
const COUNTRY_FLAGS: Record<string, string> = {
  // Europe (Eurovision-heavy region)
  finland: "🇫🇮",
  greece: "🇬🇷",
  france: "🇫🇷",
  germany: "🇩🇪",
  italy: "🇮🇹",
  spain: "🇪🇸",
  portugal: "🇵🇹",
  "united kingdom": "🇬🇧",
  uk: "🇬🇧",
  britain: "🇬🇧",
  "great britain": "🇬🇧",
  england: "🇬🇧",
  ireland: "🇮🇪",
  iceland: "🇮🇸",
  norway: "🇳🇴",
  sweden: "🇸🇪",
  denmark: "🇩🇰",
  netherlands: "🇳🇱",
  belgium: "🇧🇪",
  switzerland: "🇨🇭",
  austria: "🇦🇹",
  poland: "🇵🇱",
  czech: "🇨🇿",
  czechia: "🇨🇿",
  hungary: "🇭🇺",
  romania: "🇷🇴",
  ukraine: "🇺🇦",
  russia: "🇷🇺",
  cyprus: "🇨🇾",
  malta: "🇲🇹",
  // Americas (FIFA + political markets)
  brazil: "🇧🇷",
  brasil: "🇧🇷",
  argentina: "🇦🇷",
  uruguay: "🇺🇾",
  chile: "🇨🇱",
  colombia: "🇨🇴",
  peru: "🇵🇪",
  mexico: "🇲🇽",
  méxico: "🇲🇽",
  "united states": "🇺🇸",
  usa: "🇺🇸",
  us: "🇺🇸",
  america: "🇺🇸",
  canada: "🇨🇦",
  // Asia + Oceania + Africa (FIFA / Olympics / non-Eurovision)
  australia: "🇦🇺",
  japan: "🇯🇵",
  "south korea": "🇰🇷",
  korea: "🇰🇷",
  china: "🇨🇳",
  india: "🇮🇳",
  thailand: "🇹🇭",
  indonesia: "🇮🇩",
  turkey: "🇹🇷",
  israel: "🇮🇱",
  "saudi arabia": "🇸🇦",
  qatar: "🇶🇦",
  egypt: "🇪🇬",
  morocco: "🇲🇦",
  "south africa": "🇿🇦",
  nigeria: "🇳🇬",
};

// ─── Concept glyphs (yes/no/over/under/draw/other) ──────────────
//
// Match on the canonical key (English) AND its Portuguese / Spanish
// variants so a pt-BR-locale outcome card still picks up "Sim" /
// "Não" / "Empate".
const CONCEPT_EMOJI: Record<string, string> = {
  yes: "✓",
  sim: "✓",
  sí: "✓",
  si: "✓",
  oui: "✓",
  no: "✗",
  não: "✗",
  nao: "✗",
  non: "✗",
  other: "•",
  outros: "•",
  outro: "•",
  otro: "•",
  autre: "•",
  draw: "🤝",
  tie: "🤝",
  empate: "🤝",
  above: "▲",
  over: "▲",
  acima: "▲",
  sobre: "▲",
  below: "▼",
  under: "▼",
  abaixo: "▼",
  bajo: "▼",
};

// ─── Bubble palette ─────────────────────────────────────────────
//
// Six color stops picked from the existing token deck so the bubble
// reads consistent with the rest of the app. Hashing the outcome
// name → palette-index keeps the same outcome's bubble stable across
// renders + sessions.
const BUBBLE_COLORS = [
  "#2d8a5d", // var(--color-yes)
  "#b34a4a", // var(--color-no)
  "#3b82f6", // blue-500
  "#a855f7", // purple-500
  "#f59e0b", // amber-500
  "#06b6d4", // cyan-500
];

function hash(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

function normalize(name: string): string {
  return name.trim().toLowerCase();
}

function initials(name: string): string {
  const cleaned = name.trim();
  if (!cleaned) return "??";
  const parts = cleaned.split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return cleaned.slice(0, 2).toUpperCase();
}

/**
 * Resolve a display icon for an outcome name. Lookup chain:
 *   1. ISO-3166 country flag (English + pt/es aliases)
 *   2. Concept glyph (yes / no / other / draw / over / under)
 *   3. Fallback colored bubble with 2-letter initials
 *
 * The returned shape is a discriminated union so the row component
 * can render `<span>{emoji}</span>` for emoji icons and a styled
 * `<div>{initials}</div>` for bubble icons without branching on
 * truthy/falsy strings (which would conflate the empty-string case
 * with the bubble case).
 */
export function getOutcomeIcon(
  outcome: { name: string } | string,
): OutcomeIcon {
  const name = typeof outcome === "string" ? outcome : outcome.name;
  const key = normalize(name);
  if (key in COUNTRY_FLAGS) {
    return { kind: "emoji", emoji: COUNTRY_FLAGS[key] };
  }
  if (key in CONCEPT_EMOJI) {
    return { kind: "emoji", emoji: CONCEPT_EMOJI[key] };
  }
  const color = BUBBLE_COLORS[hash(key) % BUBBLE_COLORS.length];
  return { kind: "bubble", initials: initials(name), color };
}

/**
 * Number of distinct country-flag rows. Exported for the PR
 * description's "country-flag map size" report — keeps the count
 * grep-able and avoids re-counting commas.
 */
export const COUNTRY_FLAG_COUNT = Object.keys(COUNTRY_FLAGS).length;
